import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Message, Segment } from 'semantic-ui-react'
import debounce from 'lodash/fp/debounce'
import isEqual from 'lodash/fp/isEqual'

import { IPaginatedResponse } from '@cbgms/api/response'
import { IPaginatedRequest } from '@cbgms/api/request'
import { IGridProps } from '@cbgms/components/grid/Grid'

import { filtersToString, sortOrdersToString } from './KnockHelpers'
import { RequestContext } from '../Contexts/RequestContext'
import { IColumn } from '../Types'
import { Pagination } from '@cbgms/api'
import { ISetPaginationProperty } from '../../grid/Hooks/use-pagination-manager'

export type RefreshDataFunc = (params?: IPaginatedRequest) => Promise<any>

export interface IWithKnockDataLoaderProps extends Omit<IGridProps, 'data' | 'onFilterChange'> {
  /**
   * Called when the data for the grid has to be loaded
   */
  dataLoader: (params: IPaginatedRequest, props: any) => Promise<any>

  /**
   * This function will be called after the first time the data is loaded
   *
   * The first parameter is a function you can call to reload the data of the grid
   *
   * @param refreshData When run the data in the grid will be reloaded
   */
  onDataLoaderInitialized?: (refreshData: RefreshDataFunc) => void

  onSettingsReset: (hard?: boolean) => Promise<void>

  setPaginationProperty: (args: ISetPaginationProperty) => void

  getPaginationProperty: (propertyName: Pagination) => number | undefined

  predefinedSearch?: string
}

export interface IWithKnockLoaderState {
  isLoaded: boolean
  isFetching: boolean
  hasError: boolean
  data: IPaginatedResponse<unknown>
}

export interface IErrorMessageProps {
  refreshData: RefreshDataFunc
  onSettingsReset?: (hard?: boolean) => Promise<void>
}

const ErrorMessage: React.FC<IErrorMessageProps> = props => {
  const { t } = useTranslation()
  const { refreshData, onSettingsReset } = props

  return (
    <Message style={{ margin: 20 }}>
      <p>{t('knock_data_loader.error')}</p>
      <Button content={t('knock_data_loader.retry')} positive onClick={() => refreshData()} />
      <Button content={t('knock_data_loader.reset')} negative onClick={() => onSettingsReset && onSettingsReset(true)} />
    </Message>
  )
}

export const withKnockDataLoader = (WrappedComponent: any) => {
  class WithKnockDataLoader extends React.Component<IWithKnockDataLoaderProps, IWithKnockLoaderState> {
    constructor(props: IWithKnockDataLoaderProps) {
      super(props)

      this.state = {
        isLoaded: false,
        isFetching: false,
        hasError: false,
        data: {
          Data: [],
          Paging: {
            Page: props.getPaginationProperty(Pagination.Page) || 1,
            PerPage: props.getPaginationProperty(Pagination.PerPage) || 25,
            Total: props.getPaginationProperty(Pagination.Total) || 0,
            TotalPages: props.getPaginationProperty(Pagination.TotalPages) || 1,
            Params: {
              Fields: [],
              OrderBy: []
            }
          },
          Structure: {}
        }
      }

      this.loadData = this.loadData.bind(this)
    }
    static contextType = RequestContext

    isStateUpdateable = false

    loadDataDebounced = debounce(300, this.loadData)

    async componentDidMount() {
      this.isStateUpdateable = true
      await this.loadData()

      if (this.props.onDataLoaderInitialized) {
        this.props.onDataLoaderInitialized(this.refreshData)
      }
    }

    componentDidUpdate(prevProps: IWithKnockDataLoaderProps) {
      // Compare only the dependencies required to make the correct request.
      // Changing this might cause unforseen side effects. For example double
      // requests happening or no requests happening when they should be happening.
      const previousColumnDependencies = prevProps.headers.map(this.mapToRequestDependenciesToCompare)
      const newColumnDependencies = this.props.headers.map(this.mapToRequestDependenciesToCompare)
      const columnDependenciesChanged = !isEqual(previousColumnDependencies, newColumnDependencies)
      const predefinedSearchChanged = prevProps.predefinedSearch !== this.props.predefinedSearch

      if (columnDependenciesChanged || predefinedSearchChanged) {
        this.loadDataDebounced()
      }
    }

    mapToRequestDependenciesToCompare(header: IColumn) {
      return {
        filter: header.filter,
        filterOperator: header.filterOperator,
        key: header.key,
        sortIndex: header.sortIndex,
        sortOrder: header.sortOrder
      }
    }

    componentWillUnmount() {
      this.isStateUpdateable = false
    }

    updateState(state: IWithKnockLoaderState | ((prevState: IWithKnockLoaderState) => IWithKnockLoaderState)): void {
      if (this.isStateUpdateable) {
        this.setState(state)
      }
    }

    currentRequest: Promise<any> | null = null

    async loadData(additionalParams?: IPaginatedRequest) {
      const { dataLoader, setPaginationProperty } = this.props

      const params = this.createQueryParams(additionalParams)
      const request = dataLoader(params, this.props)

      this.currentRequest = request

      try {
        if (this.isStateUpdateable) {
          this.setState(s => ({ ...s, isFetching: true }))

          const result = await request
          if (request === this.currentRequest) {
            if (result.Paging.Total === 0 && result.Paging.Page > 1) {
              setPaginationProperty({ Page: 1 })
              this.refreshData()
              return
            }
            setPaginationProperty({ Total: result.Paging.Total })
            this.updateState(() => ({
              data: result,
              isLoaded: true,
              isFetching: false,
              hasError: false
            }))

            this.setRequestContextValues(params, result)
          }
        }
      } catch {
        if (request === this.currentRequest) {
          this.updateState(prevState => ({
            data: prevState.data,
            isLoaded: true,
            isFetching: false,
            hasError: true
          }))
        }
      }
    }

    setRequestContextValues(params: IPaginatedRequest, data: any) {
      if (this.context.search !== params.search) {
        this.context.setSearch(params.search)
      }

      if (this.context.orderBy !== params.orderBy) {
        this.context.setOrderBy(params.orderBy)
      }

      const models = data?.Structure ? Object.keys(data.Structure) : []

      const fields = models.map(model => `${model}.*`).join(',')

      if (this.context.fields !== fields) {
        this.context.setFields(fields)
      }

      const totalEntries = data?.Paging?.Total ?? 0
      if (totalEntries) {
        this.context.setTotal(totalEntries)
      }
    }

    createQueryParams(params?: IPaginatedRequest): IPaginatedRequest {
      const { headers, predefinedSearch, getPaginationProperty } = this.props
      const { data } = this.state

      const requestQuery = {
        fields: headers
          .filter(header => !header.localOnly)
          .map(header => header.key)
          .join(','),
        search: filtersToString(headers, predefinedSearch),
        orderBy: sortOrdersToString(headers),
        page: getPaginationProperty(Pagination.Page) || data.Paging.Page,
        perPage: getPaginationProperty(Pagination.PerPage) || data.Paging.PerPage
      }

      return {
        ...requestQuery,
        ...params
      }
    }

    refreshData = async () => {
      return this.loadData()
    }

    render() {
      const { isLoaded, hasError, isFetching, data } = this.state
      const { onSettingsReset, ...rest } = this.props

      return isLoaded ? (
        <WrappedComponent
          data={data}
          loadData={this.loadData}
          onSettingsReset={onSettingsReset}
          isFetching={isFetching}
          errorMessage={hasError && <ErrorMessage refreshData={this.refreshData} onSettingsReset={onSettingsReset} />}
          {...rest}
        />
      ) : (
        <Segment placeholder attached='bottom' loading />
      )
    }
  }

  return WithKnockDataLoader
}

export default withKnockDataLoader
